import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, Pause, Trash2, ExternalLink, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import CopyField from "@/components/CopyField";
import { mobileDigits } from "@/lib/phone";

const emptyForm = { slug: "", business_name: "", owner_name: "", owner_email: "", owner_phone: "", plan_code: "free_trial" };
const inputClass = "w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black";

const statusStyle = (status) => ({
  background: status === "active" ? "#DCFCE7" : status === "suspended" ? "#FEE2E2" : "#FEF3C7",
  color: status === "active" ? "#166534" : status === "suspended" ? "#991B1B" : "#854D0E",
});

export default function SuperTenants() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [created, setCreated] = useState(null);

  const load = async () => {
    const [t, p] = await Promise.all([api.get("/super/tenants"), api.get("/super/plans")]);
    setTenants(t.data); setPlans(p.data);
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/super/tenants", form);
      toast.success("Tenant created and active");
      setCreated(r.data);
      setShowForm(false); setForm(emptyForm);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Check the slug (lowercase letters, numbers and hyphens) and the admin mobile number");
    }
  };

  const approve = async (id) => { await api.post(`/super/tenants/${id}/approve`); toast.success("Activated"); load(); };
  const suspend = async (id) => { await api.post(`/super/tenants/${id}/suspend`); toast.success("Suspended"); load(); };
  const remove = async (id) => {
    if (!window.confirm("Delete tenant and ALL data?")) return;
    await api.delete(`/super/tenants/${id}`); toast.success("Deleted"); load();
  };

  const Actions = ({ t }) => (
    <div className="flex flex-wrap justify-end gap-1">
      <Link to={`/super/tenants/${t.id}`} className="inline-flex items-center gap-1.5 bg-black text-white px-3 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-800 min-h-[36px]" data-testid={`super-tenant-manage-${t.slug}`}>
        <Settings2 className="h-3.5 w-3.5" /> Manage
      </Link>
      <Link to={`/t/${t.slug}`} target="_blank" className="icon-btn hover:bg-neutral-100" title="Open customer app" aria-label="Open customer app" data-testid={`super-tenant-open-${t.slug}`}><ExternalLink className="h-4 w-4" /></Link>
      {t.status !== "active" && <button onClick={() => approve(t.id)} className="icon-btn hover:bg-green-50 text-green-700" title="Activate" aria-label="Activate" data-testid={`super-tenant-approve-${t.slug}`}><Check className="h-4 w-4" /></button>}
      {t.status === "active" && <button onClick={() => suspend(t.id)} className="icon-btn hover:bg-yellow-50 text-yellow-700" title="Suspend" aria-label="Suspend"><Pause className="h-4 w-4" /></button>}
      {!t.is_demo && <button onClick={() => remove(t.id)} className="icon-btn hover:bg-red-50 text-red-700" title="Delete" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
    </div>
  );

  return (
    <div data-testid="super-tenants">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="admin-overline">Roster</div>
          <h1 className="admin-heading text-3xl font-bold mt-1">Tenants</h1>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="bg-black text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-800 inline-flex items-center gap-2 min-h-[44px]" data-testid="super-tenant-new-btn">
          <Plus className="h-3.5 w-3.5" /> New tenant
        </button>
      </div>

      {created?.admin_login && (
        <div className="bg-white border-2 border-black p-4 sm:p-6 mb-6 space-y-4" data-testid="super-tenant-created">
          <div>
            <div className="admin-overline">Tenant admin login for {created.business_name}</div>
            <p className="text-sm text-neutral-700 mt-1">The business owner signs in with this mobile number and a one-time code sent to it. There is no password. Send them the login address.</p>
            <p className="text-sm text-neutral-700 mt-1" data-testid="super-tenant-created-legal">Its Privacy Policy, Terms & Conditions and Account Deletion pages are already live with {created.business_name}'s own name. Nothing needs to be written; copy their addresses from "Set up this tenant".</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <CopyField label="Admin mobile number" value={created.admin_login.phone} testid="super-tenant-created-phone" />
            <CopyField label="Admin login address" value={`${window.location.origin}/t/${created.slug}/admin/login`} testid="super-tenant-created-login-url" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/super/tenants/${created.id}`} className="bg-black text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">Set up this tenant</Link>
            <button onClick={() => setCreated(null)} className="border border-neutral-300 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">Done</button>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={onCreate} className="bg-white border border-neutral-200 p-4 sm:p-6 mb-6 grid md:grid-cols-3 gap-4" data-testid="super-tenant-form">
          <div>
            <label className="admin-overline">Slug</label>
            <input required value={form.slug} pattern="[a-z0-9]([a-z0-9\-]{0,38}[a-z0-9])?" maxLength={40}
              title="Lowercase letters, numbers and hyphens. Must start and end with a letter or number."
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
              className={`${inputClass} font-mono`} placeholder="my-business" data-testid="super-tenant-form-slug" />
            <div className="text-[11px] text-neutral-500 mt-1 break-anywhere">URL: {window.location.origin}/t/{form.slug || "…"}</div>
          </div>
          <div><label className="admin-overline">Business name</label><input required maxLength={80} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className={inputClass} data-testid="super-tenant-form-name" /></div>
          <div><label className="admin-overline">Plan</label>
            <select value={form.plan_code} onChange={(e) => setForm({ ...form, plan_code: e.target.value })} className={`${inputClass} bg-white`} data-testid="super-tenant-form-plan">
              {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
            </select>
          </div>
          <div><label className="admin-overline">Owner name</label><input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className={inputClass} /></div>
          <div><label className="admin-overline">Owner email</label><input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className={inputClass} data-testid="super-tenant-form-email" /></div>
          <div><label className="admin-overline" htmlFor="tenant-owner-phone">Admin mobile number</label>
            <input id="tenant-owner-phone" type="tel" inputMode="numeric" required pattern="[6-9][0-9]{9}" title="10-digit mobile number, without +91" value={form.owner_phone}
              onChange={(e) => setForm({ ...form, owner_phone: mobileDigits(e.target.value) })}
              className={inputClass} placeholder="98XXXXXXXX" data-testid="super-tenant-form-phone" />
            <div className="text-[11px] text-neutral-500 mt-1">10 digits, without +91.</div></div>
          <div className="md:col-span-3 text-xs text-neutral-500">The tenant is active as soon as it is created. Its admin signs in with the mobile number above and a one-time code sent by SMS or WhatsApp, so it must be the owner's real number.</div>
          <div className="md:col-span-3 flex flex-wrap gap-2">
            <button type="submit" className="bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider min-h-[44px]" data-testid="super-tenant-form-submit">Create</button>
            <button type="button" onClick={() => setShowForm(false)} className="border border-neutral-300 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider min-h-[44px]">Cancel</button>
          </div>
        </form>
      )}

      {/* Phones and tablets: one card per tenant */}
      <div className="lg:hidden space-y-3" data-testid="super-tenants-cards">
        {tenants.map((t) => (
          <div key={t.id} className="bg-white border border-neutral-200 p-4">
            <div className="flex justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold break-words">{t.business_name}</div>
                <div className="font-mono text-xs text-neutral-500 break-anywhere">/t/{t.slug}</div>
              </div>
              <span className="status-pill self-start flex-shrink-0" style={statusStyle(t.status)}>{t.status}</span>
            </div>
            <div className="text-xs text-neutral-600 mt-2 break-anywhere">{t.plan_code} · {t.owner_email || t.owner_phone || "no owner contact"}</div>
            <div className="mt-3"><Actions t={t} /></div>
          </div>
        ))}
      </div>

      <div className="hidden lg:block bg-white border border-neutral-200 table-scroll" data-testid="super-tenants-table">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Tenant</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Slug</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Plan</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Status</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold">Owner</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wider font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`super-tenant-row-${t.slug}`}>
                <td className="px-4 py-3 font-semibold">{t.business_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{t.slug}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 bg-neutral-100 text-xs">{t.plan_code}</span></td>
                <td className="px-4 py-3"><span className="status-pill" style={statusStyle(t.status)}>{t.status}</span></td>
                <td className="px-4 py-3 text-xs text-neutral-600 break-anywhere">{t.owner_email || t.owner_phone || "—"}</td>
                <td className="px-4 py-3"><Actions t={t} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
